<script setup lang="ts">
import type { EditorState, SerializedEditorState } from "lexical";
import {
  AutoLinkPlugin,
  CheckListPlugin,
  LinkPlugin as LexicalLinkPlugin,
  ListPlugin,
  MarkdownShortcutPlugin,
} from "lexical-vue";
import { computed, ref } from "vue";

import type { Property } from "~/shared/types";

import { FORM_TELEPORT_KEY, URL_MATCHERS } from "./const";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin.vue";
import CodeSelectorPlugin from "./plugins/CodeSelectorPlugin.vue";
import ColorPlugin from "./plugins/ColorPlugin.vue";
import FloatingToolbar from "./plugins/FloatingToolbar.vue";
import ManageTextFormatPlugin from "./plugins/ManageTextFormatPlugin.vue";
import OnChangePlugin from "./plugins/OnChangePlugin.vue";
import PasteMarkdownPlugin from "./plugins/PasteMarkdownPlugin.vue";
import PlaceholderPlugin from "./plugins/PlaceholderPlugin.vue";
import QuoteSectionPlugin from "./plugins/QuoteSectionPlugin.vue";
import ShortcutsPlugin from "./plugins/ShortcutsPlugin.vue";
import TablePlugin from "./plugins/TablePlugin.vue";
import TabPlugin from "./plugins/TabPlugin.vue";
import TextEditor from "./TextEditor.vue";
import { TRANSFORMERS } from "./transformers";
import { GENERIC_EDITOR_NODES } from "./utils";

const props = defineProps<{
  property: Property;
  value?: SerializedEditorState;
}>();

const emit = defineEmits<{
  update: [text: SerializedEditorState];
}>();

const textEditor = ref<InstanceType<typeof TextEditor> | null>(null);

const namespace = computed(() => `default-${props.property.duid}`);

const onEditorChange = (newEditorState: EditorState) => {
  emit("update", newEditorState.toJSON());
};

const focus = () => {
  textEditor.value?.focus();
};

defineExpose({
  focus,
});
</script>

<template>
  <TextEditor
    ref="textEditor"
    :namespace="namespace"
    :nodes="GENERIC_EDITOR_NODES"
    :initial-state="value"
    editor-classes="cursor-text"
    placeholder="Add a description"
    :left-margin-px="8"
    high-contrast-border
    :min-height-px="100">
    <template #default>
      <!-- lexical-vue plugins -->
      <AutoLinkPlugin :matchers="URL_MATCHERS" />
      <CheckListPlugin />
      <LexicalLinkPlugin />
      <ListPlugin />
      <MarkdownShortcutPlugin :transformers="TRANSFORMERS" />
      <!-- custom plugins -->
      <CodeHighlightPlugin />
      <CodeSelectorPlugin editable />
      <ColorPlugin />
      <FloatingToolbar hide-ai-action-button :teleport-key="FORM_TELEPORT_KEY" />
      <ManageTextFormatPlugin />
      <OnChangePlugin :has-focus="Boolean(textEditor?.hasFocus)" @change="onEditorChange" />
      <PasteMarkdownPlugin />
      <PlaceholderPlugin no-ai />
      <QuoteSectionPlugin />
      <ShortcutsPlugin />
      <TablePlugin />
      <TabPlugin />
    </template>
  </TextEditor>
</template>
